import { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Drawer,
  FileButton,
  Group,
  Paper,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconAlertTriangle, IconPaperclip, IconPlus } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import dayjs from 'dayjs';
import { CONCEPTOS_GASTO, hoy, TIPOS_FACTURA_RECIBIDA } from '@gestionpyme/dominio';
import { api } from '../api';
import {
  avisarError,
  avisarOk,
  Cabecera,
  CampoEuros,
  CampoPorcentaje,
  conRectificacion,
  EJERCICIO_ACTUAL,
  EstadoMovimiento,
  euros,
  fechaCorta,
  mostrarAvisos,
  nombreFormaPago,
  OPCIONES_CONCEPTO_GASTO,
  OPCIONES_EJERCICIO,
  OPCIONES_FORMA_PAGO,
  OPCIONES_IGIC,
} from '../comun';
import { SelectorTercero } from '../componentes/Terceros';
import { ACEPTA_ARCHIVOS, Adjuntos, subirDocumento } from '../componentes/Adjuntos';
import { Dato } from './Ingresos';
import type { DetalleMovimiento, Gasto } from '../tipos';

const aISO = (d: Date | string | null) => (d ? dayjs(d).format('YYYY-MM-DD') : null);
const OPCIONES_TIPO = TIPOS_FACTURA_RECIBIDA.lista
  .filter((t) => ['F1', 'F2', 'F6', 'R1', 'R4', 'R5', 'SF'].includes(t.codigo))
  .map((t) => ({ value: t.codigo, label: `${t.codigo} · ${t.nombre}` }));

export function PaginaGastos() {
  const [params] = useSearchParams();
  const [ejercicio, setEjercicio] = useState(params.get('ejercicio') ?? String(EJERCICIO_ACTUAL));
  const [periodo, setPeriodo] = useState<string | null>(params.get('periodo'));
  const [estado, setEstado] = useState('todos');
  const [q, setQ] = useState(params.get('q') ?? '');
  const [panel, setPanel] = useState<null | { tipo: 'nuevo' } | { tipo: 'detalle'; id: string }>(null);

  const consulta = new URLSearchParams({ ejercicio, estado, ...(periodo ? { periodo } : {}), ...(q ? { q } : {}) });
  const lista = useQuery({ queryKey: ['gastos', ejercicio, periodo, estado, q], queryFn: () => api.get<Gasto[]>(`/gastos?${consulta}`) });
  const filas = (lista.data ?? []).filter((g) => (params.get('isp') ? g.inversionSujetoPasivo : true));
  const contables = filas.filter((g) => g.estado === 'activo');
  const pendientes = filas.filter((g) => g.estado === 'pendiente_justificar').length;

  return (
    <>
      <Cabecera titulo="Gastos y compras" descripcion="Facturas recibidas, tickets y justificantes">
        <Button leftSection={<IconPlus size={16} />} onClick={() => setPanel({ tipo: 'nuevo' })}>
          Nuevo gasto
        </Button>
      </Cabecera>

      <Paper withBorder p="sm" mb="md">
        <Group gap="sm" wrap="wrap">
          <Select w={110} data={OPCIONES_EJERCICIO} value={ejercicio} onChange={(v) => setEjercicio(v ?? String(EJERCICIO_ACTUAL))} allowDeselect={false} aria-label="Ejercicio" />
          <Select w={130} placeholder="Trimestre" data={['1T', '2T', '3T', '4T']} value={periodo} onChange={setPeriodo} clearable aria-label="Trimestre" />
          <SegmentedControl
            size="xs"
            value={estado}
            onChange={setEstado}
            data={[
              { value: 'todos', label: 'Todos' },
              { value: 'pendiente_justificar', label: `Pendientes${pendientes ? ` (${pendientes})` : ''}` },
              { value: 'anulado', label: 'Anulados' },
            ]}
          />
          <TextInput placeholder="Buscar proveedor, número…" value={q} onChange={(e) => setQ(e.currentTarget.value)} style={{ flex: 1, minWidth: 180 }} />
          <Text fw={600}>
            {contables.length} · {euros(contables.reduce((s, g) => s + g.total, 0))}
          </Text>
        </Group>
      </Paper>

      <Paper withBorder>
        <Table.ScrollContainer minWidth={680}>
          <Table highlightOnHover verticalSpacing="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Fecha</Table.Th>
                <Table.Th>Proveedor</Table.Th>
                <Table.Th>Concepto</Table.Th>
                <Table.Th>Pago</Table.Th>
                <Table.Th ta="right">Total</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filas.map((g) => (
                <Table.Tr key={g.id} style={{ cursor: 'pointer', opacity: g.estado === 'anulado' ? 0.5 : 1 }} onClick={() => setPanel({ tipo: 'detalle', id: g.id })}>
                  <Table.Td>{fechaCorta(g.fechaExpedicion)}</Table.Td>
                  <Table.Td>
                    <Text size="sm" lineClamp={1}>
                      {g.terceroNombre ?? g.descripcion ?? '—'}
                    </Text>
                    {g.numeroFactura && (
                      <Text size="xs" c="dimmed">
                        {g.numeroFactura}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Tooltip label={g.concepto ? CONCEPTOS_GASTO.nombre(g.concepto) : 'No deducible'}>
                      <Badge variant="outline" size="sm" color={g.concepto ? 'blue' : 'gray'}>
                        {g.concepto ?? 'No deduc.'}
                      </Badge>
                    </Tooltip>
                  </Table.Td>
                  <Table.Td>{nombreFormaPago(g.formaPago)}</Table.Td>
                  <Table.Td ta="right" fw={500}>
                    {euros(g.total)}
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4} wrap="nowrap">
                      {g.estado !== 'activo' && <EstadoMovimiento estado={g.estado} />}
                      {g.adjuntos === 0 && g.estado === 'activo' && (
                        <Tooltip label="Sin factura adjunta">
                          <IconPaperclip size={16} color="var(--mantine-color-orange-6)" />
                        </Tooltip>
                      )}
                      {g.inversionSujetoPasivo && (
                        <Tooltip label="Proveedor no establecido en Canarias: posible modelo 412">
                          <IconAlertTriangle size={16} color="var(--mantine-color-yellow-7)" />
                        </Tooltip>
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        {filas.length === 0 && (
          <Text c="dimmed" ta="center" p="lg">
            No hay gastos en este periodo.
          </Text>
        )}
      </Paper>

      <Drawer opened={panel !== null} onClose={() => setPanel(null)} position="right" size="lg" title={panel?.tipo === 'nuevo' ? 'Nuevo gasto' : 'Gasto'}>
        {panel?.tipo === 'nuevo' && <FormularioGasto onListo={() => setPanel(null)} />}
        {panel?.tipo === 'detalle' && <DetalleGasto id={panel.id} onListo={() => setPanel(null)} />}
      </Drawer>
    </>
  );
}

interface DatosGasto {
  fechaExpedicion: string | null;
  tipoFactura: string;
  numeroFactura: string;
  terceroId: string | null;
  concepto: string | null;
  base: number | null;
  tipoImpuesto: number;
  porcentajeDeducible: number | null;
  formaPago: string | null;
  descripcion: string;
  pendienteJustificar: boolean;
  inversionSujetoPasivo: boolean | undefined;
}

function FormularioGasto({ inicial, id, onListo }: { inicial?: DatosGasto; id?: string; onListo: () => void }) {
  const qc = useQueryClient();
  const [d, setD] = useState<DatosGasto>(
    inicial ?? {
      fechaExpedicion: hoy(),
      tipoFactura: 'F1',
      numeroFactura: '',
      terceroId: null,
      concepto: 'G01',
      base: null,
      tipoImpuesto: 0,
      porcentajeDeducible: 10000,
      formaPago: 'tarjeta',
      descripcion: '',
      pendienteJustificar: false,
      inversionSujetoPasivo: undefined,
    },
  );
  // Actualización funcional: evita perder lo escrito si un callback asíncrono usa un estado antiguo.
  const cambiar = (c: Partial<DatosGasto>) => setD((p) => ({ ...p, ...c }));
  const [archivo, setArchivo] = useState<File | null>(null);
  const [enviando, setEnviando] = useState(false);
  const reinicioArchivo = useRef<() => void>(null);
  const cuota = d.base ? Math.round((d.base * d.tipoImpuesto) / 10000) : 0;

  const guardar = async () => {
    setEnviando(true);
    try {
      const cuerpo = (rectificacion?: { motivo: string }) => ({
        fechaExpedicion: d.fechaExpedicion,
        tipoFactura: d.tipoFactura,
        numeroFactura: d.numeroFactura || null,
        terceroId: d.terceroId,
        concepto: d.concepto,
        porcentajeDeducible: d.porcentajeDeducible ?? 10000,
        formaPago: d.formaPago,
        descripcion: d.descripcion || null,
        pendienteJustificar: d.pendienteJustificar,
        inversionSujetoPasivo: d.inversionSujetoPasivo,
        lineas: [{ baseImponible: d.base, tipoImpuesto: d.tipoImpuesto }],
        rectificacion,
      });
      const g = await conRectificacion((rect) =>
        id ? api.put<Gasto & { avisos: string[] }>(`/gastos/${id}`, cuerpo(rect)) : api.post<Gasto & { avisos: string[] }>('/gastos', cuerpo(rect)),
      );
      if (!g) return;
      mostrarAvisos(g.avisos);
      if (archivo) {
        await subirDocumento(archivo, {
          categoria: 'justificante_gasto',
          titulo: `Factura ${d.numeroFactura || d.descripcion || ''}`.trim(),
          fechaDocumento: d.fechaExpedicion,
          entidad: 'gasto',
          entidadId: g.id,
        });
      }
      avisarOk(id ? 'Gasto actualizado' : 'Gasto registrado');
      await qc.invalidateQueries({ queryKey: ['gastos'] });
      onListo();
    } catch (e) {
      avisarError(e);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Stack>
      <SimpleGrid cols={{ base: 1, xs: 2 }}>
        <DateInput label="Fecha de la factura" value={d.fechaExpedicion} onChange={(v) => cambiar({ fechaExpedicion: aISO(v) })} valueFormat="DD/MM/YYYY" required />
        <Select label="Tipo de documento" data={OPCIONES_TIPO} value={d.tipoFactura} onChange={(v) => cambiar({ tipoFactura: v ?? 'F1' })} allowDeselect={false} />
      </SimpleGrid>
      <SelectorTercero tipo="proveedor" label="Proveedor" valor={d.terceroId} onCambio={(v) => cambiar({ terceroId: v, inversionSujetoPasivo: undefined })} />
      <SimpleGrid cols={{ base: 1, xs: 2 }}>
        <TextInput label="Número de factura" value={d.numeroFactura} onChange={(e) => cambiar({ numeroFactura: e.currentTarget.value })} />
        <Select label="Concepto (IRPF)" data={OPCIONES_CONCEPTO_GASTO} value={d.concepto} onChange={(v) => cambiar({ concepto: v })} searchable clearable placeholder="No deducible" />
      </SimpleGrid>
      <Textarea label="Descripción" autosize minRows={1} value={d.descripcion} onChange={(e) => cambiar({ descripcion: e.currentTarget.value })} placeholder="Telas, hilos, puesto del mercadillo…" />
      <SimpleGrid cols={{ base: 1, xs: 3 }}>
        <CampoEuros label="Base imponible" required valor={d.base} onCambio={(c) => cambiar({ base: c })} />
        <Select label="IGIC de la factura" data={OPCIONES_IGIC} value={String(d.tipoImpuesto)} onChange={(v) => cambiar({ tipoImpuesto: Number(v ?? 0) })} allowDeselect={false} />
        <Select label="Forma de pago" data={OPCIONES_FORMA_PAGO} value={d.formaPago} onChange={(v) => cambiar({ formaPago: v })} clearable />
      </SimpleGrid>
      <Text size="xs" c="dimmed">
        En el REPEP el IGIC pagado no se deduce: la aplicación lo suma al gasto deducible del IRPF. Total: <b>{euros((d.base ?? 0) + cuota)}</b>
      </Text>
      <CampoPorcentaje
        label="Parte deducible"
        description="100 % salvo uso mixto (p. ej. el móvil personal y del negocio)"
        valor={d.porcentajeDeducible}
        onCambio={(v) => cambiar({ porcentajeDeducible: v })}
        w={200}
      />
      <Checkbox
        label="Aún no tengo la factura (pendiente de justificar)"
        description="No contará como gasto deducible hasta que la adjuntes"
        checked={d.pendienteJustificar}
        onChange={(e) => cambiar({ pendienteJustificar: e.currentTarget.checked })}
      />
      {id && (
        <Checkbox
          label="Posible inversión del sujeto pasivo (proveedor no establecido en Canarias)"
          checked={d.inversionSujetoPasivo ?? false}
          onChange={(e) => cambiar({ inversionSujetoPasivo: e.currentTarget.checked })}
        />
      )}
      {!id && (
        <Group>
          <FileButton onChange={setArchivo} accept={ACEPTA_ARCHIVOS} resetRef={reinicioArchivo}>
            {(props) => (
              <Button variant="light" leftSection={<IconPaperclip size={16} />} {...props}>
                {archivo ? archivo.name : 'Adjuntar factura o foto del ticket'}
              </Button>
            )}
          </FileButton>
          {archivo && (
            <Button variant="subtle" color="gray" onClick={() => { setArchivo(null); reinicioArchivo.current?.(); }}>
              Quitar
            </Button>
          )}
        </Group>
      )}
      <Group justify="flex-end">
        <Button onClick={guardar} loading={enviando} disabled={!d.fechaExpedicion || !d.base}>
          Guardar
        </Button>
      </Group>
    </Stack>
  );
}

function DetalleGasto({ id, onListo }: { id: string; onListo: () => void }) {
  const qc = useQueryClient();
  const det = useQuery({ queryKey: ['gastos', 'detalle', id], queryFn: () => api.get<Gasto & DetalleMovimiento>(`/gastos/${id}`) });
  const [editando, setEditando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const g = det.data;
  const inicial = useMemo<DatosGasto | undefined>(
    () =>
      g && {
        fechaExpedicion: g.fechaExpedicion,
        tipoFactura: g.tipoFactura,
        numeroFactura: g.numeroFactura ?? '',
        terceroId: g.terceroId,
        concepto: g.concepto,
        base: g.totales.base,
        tipoImpuesto: g.lineas[0]?.tipoImpuesto ?? 0,
        porcentajeDeducible: g.porcentajeDeducible,
        formaPago: g.formaPago,
        descripcion: g.descripcion ?? '',
        pendienteJustificar: g.estado === 'pendiente_justificar',
        inversionSujetoPasivo: g.inversionSujetoPasivo,
      },
    [g],
  );
  if (!g) return <Text>Cargando…</Text>;
  if (editando) return <FormularioGasto inicial={inicial} id={id} onListo={onListo} />;

  const accion = async (ruta: string, cuerpo: Record<string, unknown>, ok: string) => {
    try {
      const r = await conRectificacion((rectificacion) => api.post(ruta, { ...cuerpo, rectificacion }));
      if (!r) return;
      avisarOk(ok);
      await qc.invalidateQueries({ queryKey: ['gastos'] });
      onListo();
    } catch (e) {
      avisarError(e);
    }
  };

  return (
    <Stack>
      <Group justify="space-between">
        <Text fw={700} fz="xl">
          {euros(g.totales.total)}
        </Text>
        <EstadoMovimiento estado={g.estado} />
      </Group>
      {g.inversionSujetoPasivo && (
        <Alert color="yellow" icon={<IconAlertTriangle size={18} />}>
          Proveedor no establecido en Canarias: puede haber inversión del sujeto pasivo del IGIC y obligación de presentar el modelo 412. Pendiente de confirmar con tu asesor.
        </Alert>
      )}
      <SimpleGrid cols={2} spacing="xs">
        <Dato etiqueta="Fecha" valor={fechaCorta(g.fechaExpedicion)} />
        <Dato etiqueta="Periodo" valor={`${g.periodo} ${g.ejercicio}`} />
        <Dato etiqueta="Proveedor" valor={g.tercero?.nombre ?? '—'} />
        <Dato etiqueta="Número" valor={g.numeroFactura ?? '—'} />
        <Dato etiqueta="Concepto" valor={g.concepto ? `${g.concepto} · ${CONCEPTOS_GASTO.nombre(g.concepto)}` : 'No deducible'} />
        <Dato etiqueta="Pago" valor={nombreFormaPago(g.formaPago)} />
        <Dato etiqueta="Base" valor={euros(g.totales.base)} />
        <Dato etiqueta="IGIC" valor={`${euros(g.totales.cuota)} (${g.impuestoDeducible ? 'deducible' : 'no deducible: suma al gasto'})`} />
      </SimpleGrid>
      {g.descripcion && <Text size="sm">{g.descripcion}</Text>}
      {g.estado === 'anulado' && <Alert color="gray">Anulado: {g.motivoAnulacion}</Alert>}
      {g.estado !== 'anulado' && (
        <>
          <Adjuntos entidad="gasto" entidadId={id} categoria="justificante_gasto" titulo={`Factura ${g.numeroFactura ?? g.tercero?.nombre ?? ''}`.trim()} fecha={g.fechaExpedicion} />
          <Group>
            {g.estado === 'pendiente_justificar' && <Button onClick={() => void accion(`/gastos/${id}/justificar`, {}, 'Gasto justificado')}>Marcar como justificado</Button>}
            <Button variant="light" onClick={() => setEditando(true)}>
              Modificar
            </Button>
          </Group>
          <Paper withBorder p="sm">
            <Stack gap="xs">
              <Text size="sm" fw={600}>
                Anular
              </Text>
              <Group align="flex-end">
                <TextInput placeholder="Motivo" value={motivo} onChange={(e) => setMotivo(e.currentTarget.value)} style={{ flex: 1 }} />
                <Button color="red" variant="light" disabled={motivo.trim().length < 3} onClick={() => void accion(`/gastos/${id}/anular`, { motivo }, 'Gasto anulado')}>
                  Anular
                </Button>
              </Group>
            </Stack>
          </Paper>
        </>
      )}
    </Stack>
  );
}
